export type FormattedValidationErrors<T> = {
  [K in keyof T]?: string;
};

interface ZodValidationError {
  properties: {
    [key: string]: { errors: string[] };
  };
}

interface ServerValidationErrors {
  errors: ServerValidationError[];
}

interface ServerValidationError {
  source: {
    pointer: string;
  };
  detail: string;
}

function isServerValidationErrors(
  value: unknown
): value is ServerValidationErrors {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const errors = (value as { errors?: unknown }).errors;

  if (!Array.isArray(errors)) {
    return false;
  }

  return errors.some((error) => {
    return (
      !!error &&
      typeof error === 'object' &&
      'source' in (error as object) &&
      'detail' in (error as object)
    );
  });
}

interface ZodIssueLike {
  path?: (string | number)[];
  message?: unknown;
}

function extractIssues(value: unknown): ZodIssueLike[] | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const asAny = value as { issues?: unknown; errors?: unknown };

  if (Array.isArray(asAny.issues)) {
    return asAny.issues as ZodIssueLike[];
  }

  if (Array.isArray(asAny.errors)) {
    return asAny.errors as ZodIssueLike[];
  }

  return null;
}

export default function formatErrors<T>(
  validation: unknown
): FormattedValidationErrors<T> {
  if (
    validation &&
    typeof validation === 'object' &&
    'properties' in validation
  ) {
    const props = (validation as ZodValidationError).properties;
    const result: FormattedValidationErrors<T> = {};

    for (const [key, value] of Object.entries(props)) {
      if (value?.errors?.length) {
        result[key as keyof T] = value.errors[0];
      }
    }

    return result;
  }

  if (isServerValidationErrors(validation)) {
    return validation.errors.reduce(
      (acc: FormattedValidationErrors<T>, error: ServerValidationError) => {
        if (error.source?.pointer) {
          const field = error.source.pointer.replace('/data/attributes/', '');
          acc[field as keyof T] = error.detail;
        }

        return acc;
      },
      {}
    );
  }

  const issues = extractIssues(validation);

  if (issues) {
    const result: FormattedValidationErrors<T> = {};

    for (const issue of issues) {
      if (!issue || typeof issue !== 'object') {
        continue;
      }

      const path = Array.isArray(issue.path) ? issue.path : [];
      const last = path[path.length - 1];
      const field =
        typeof last === 'string' || typeof last === 'number'
          ? String(last)
          : undefined;

      if (!field) {
        continue;
      }

      const message =
        typeof issue.message === 'string'
          ? issue.message
          : issue.message != null && typeof issue.message === 'object'
            ? JSON.stringify(issue.message)
            : '';

      if (!message) {
        continue;
      }

      // keep only the first error per field
      if (!result[field as keyof T]) {
        result[field as keyof T] = message;
      }
    }

    return result;
  }

  return {};
}

export { formatErrors };
